import React from 'react';
import {
	CORE_PROJECT,
	SERVICE_DESK_PROJECT,
	SOFTWARE_PROJECT,
} from '@jira/platform__project-types/src/project-types.tsx';
import { ForgeScreenEvent } from '@atlassian/jira-forge-ui-analytics/src/common/ui/index.tsx';
import { TriggerAnalyticsWrapper } from '@atlassian/jira-forge-ui-analytics/src/ui/project-page/index.tsx';
import { PROJECT_PAGE_MODULE } from '@atlassian/jira-forge-ui-constants/src/constants.tsx';
import type { ProjectPage } from '@atlassian/jira-forge-ui-types/src/common/types/extension.tsx';
import { parseExtensionId } from '@atlassian/jira-forge-ui-utils-internal/src/utils/parse-extension-id/index.tsx';
import { toSections } from '@atlassian/jira-navigation-apps-sidebar-forge/src/common/utils/subpages/index.tsx';
import { useProjectForgeAppsNav4 } from '@atlassian/jira-navigation-apps-sidebar-forge/src/controllers/project/index.tsx';
import { PROJECTS_NAVIGATION_ITEM } from '@atlassian/jira-navigation-apps-sidebar-nav4-analytics/src/common/constants/analytics/action-subject-id.tsx';
import { FIXED_ITEM } from '@atlassian/jira-navigation-apps-sidebar-nav4-analytics/src/common/constants/analytics/component-type.tsx';
import { useProjectContext } from '@atlassian/jira-providers-project-context/src/index.tsx';
import type { MoreNavMenuButtonProps } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-common-core/src/ui/more-nav-menu-button/types.tsx';
import { componentWithCondition } from '@atlassian/jira-feature-flagging-utils';
import { fg } from '@atlassian/jira-feature-gating';
import { ForgeItem } from '../../common/ui/forge-item/index.tsx';
import type { ForgeItemProps, ForgeProjectSectionProps } from '../../common/types.tsx';

const projectTypeRouteMap: Record<string, string> = {
	[CORE_PROJECT]: 'core',
	[SOFTWARE_PROJECT]: 'software',
	[SERVICE_DESK_PROJECT]: 'servicedesk',
};

export const getItemHref = (extension: ProjectPage, projectType: string, projectKey: string) => {
	const { appId, envId } = parseExtensionId(extension.id);
	return `/jira/${projectTypeRouteMap[projectType]}/projects/${projectKey}/apps/${appId}/${envId}`;
};

export const analytics: ForgeItemProps['analytics'] = {
	actionSubjectId: PROJECTS_NAVIGATION_ITEM,
	componentType: FIXED_ITEM,
	level: 2,
};

const ForgeProjectSectionOld = ({ overrides, projectKey }: ForgeProjectSectionProps) => {
	const { data } = useProjectForgeAppsNav4(projectKey);
	const { data: projectContext } = useProjectContext({
		matchParams: { projectKey },
	});

	if (!data?.length || !projectKey || !projectContext) return null;

	const { projectType } = projectContext;

	const filteredForgeAppsData =
		overrides?.showSelectedAppMenuOnly && overrides?.selectedApp != null
			? [overrides.selectedApp]
			: data;

	return (
		<TriggerAnalyticsWrapper>
			{filteredForgeAppsData.map((extension) => (
				<ForgeItem
					key={extension.id}
					id={extension.id}
					analytics={analytics}
					environmentType={extension.environmentType}
					environmentKey={extension.environmentKey}
					url={getItemHref(extension, projectType, projectKey)}
					name={extension.properties.title}
					sections={toSections(extension)}
					iconUrl={extension.properties.icon}
					showIcon
					overrides={overrides}
				/>
			))}
			<ForgeScreenEvent
				attributes={{
					moduleType: PROJECT_PAGE_MODULE,
				}}
			/>
		</TriggerAnalyticsWrapper>
	);
};

export const useForgeProjectSectionAppsData = ({
	overrides,
	projectKey,
}: ForgeProjectSectionProps) => {
	const { data } = useProjectForgeAppsNav4(projectKey);

	if (!data?.length || !projectKey) {
		return null;
	}

	return overrides?.showSelectedAppMenuOnly && overrides?.selectedApp != null
		? [overrides.selectedApp]
		: data;
};

export const renderForgeProjectSectionItem = ({
	extension,
	projectType,
	projectKey,
	overrides,
	HideFromSidebarButton,
}: {
	extension: ProjectPage;
	projectType: string;
	projectKey: string;
	overrides?: ForgeProjectSectionProps['overrides'];
	HideFromSidebarButton?: MoreNavMenuButtonProps['MenuItems'];
}): React.JSX.Element => (
	<ForgeItem
		key={extension.id}
		id={extension.id}
		analytics={analytics}
		environmentType={extension.environmentType}
		environmentKey={extension.environmentKey}
		HideFromSidebarButton={HideFromSidebarButton}
		url={getItemHref(extension, projectType, projectKey)}
		name={extension.properties.title}
		sections={toSections(extension)}
		iconUrl={extension.properties.icon}
		showIcon
		overrides={overrides}
	/>
);

const ForgeProjectSectionNew = ({ overrides, projectKey }: ForgeProjectSectionProps) => {
	const filteredForgeAppsData = useForgeProjectSectionAppsData({ overrides, projectKey });
	const { data: projectContext } = useProjectContext({
		matchParams: { projectKey },
	});

	if (filteredForgeAppsData === null || !projectContext) {
		return null;
	}

	const { projectType } = projectContext;

	return (
		<TriggerAnalyticsWrapper>
			{filteredForgeAppsData.map((extension) =>
				renderForgeProjectSectionItem({
					extension,
					projectType,
					projectKey,
					overrides,
				}),
			)}
			<ForgeScreenEvent
				attributes={{
					moduleType: PROJECT_PAGE_MODULE,
				}}
			/>
		</TriggerAnalyticsWrapper>
	);
};

export const ForgeProjectSection = componentWithCondition(
	() => fg('more_for_nav_4_level_3'),
	ForgeProjectSectionNew,
	ForgeProjectSectionOld,
);
